import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React, {useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {Permissions} from 'auth/PermissionConstants';
import FileState from 'filestate/FileState';
import notifications from 'notifications';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import getTestTimestampProps from 'test/lib/cypress/getTestTimestampProps';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import usePrevious from 'ui-library/hooks/usePrevious';
import * as DateUtil from 'util/DateUtil';
import * as VersionHistoryUtil from 'util/VersionHistoryUtil';

interface ClonedFromInfo {
  name: string;
  id: string;
  versionId: number;
  modifiedAt: string;
}

export interface Version {
  id: number | string;
  modified_at?: string;
  modifiedAt?: string;
  created_at?: string;
  fetching?: boolean;
  name?: string;
  creator: HasAvatar;
  parentId: number | string | null;
}

export interface HistoryPanelComponentProps {
  openVersion: (versionId: string | number) => void;
  cloneFromVersion?: () => void;
  resetToVersion?: () => void;
  versions: Array<Version>;
  openVersionId: string | number;
  dirty?: boolean;
  isLatest?: boolean;
  isLockedForReview?: boolean;
  disableHistoryChanges?: boolean;
  isFirstTrackedVersion?: (version: Version) => boolean;
  unsaved?: boolean;
  showNames?: boolean;
  hasParent: boolean;
  parent: ClonedFromInfo | null;
  permissions?: Permissions | null;
  showSeconds?: boolean;
  versionsWereTruncated?: boolean;
}

const fileStates = require('FileStates').default.get();

function versionToTime(version: Version, showSeconds: boolean): string {
  const options = {showYear: true, showTime: true, showSeconds};
  const date = new Date((version.modified_at || version.modifiedAt)!);
  return DateUtil.readableTime(date, options);
}

export default function HistoryPanelComponent({
  dirty = false,
  showNames = false,
  showSeconds = false,
  ...props
}: HistoryPanelComponentProps): JSX.Element {
  const [showReverted, setShowReverted] = useState(false);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const tRowRefs = useRef<Record<string, HTMLTableRowElement>>({});
  const prevProps = usePrevious(props);
  const prevShowReverted = usePrevious(showReverted);
  useEffect(() => {
    if (
      props.openVersionId !== prevProps?.openVersionId ||
      showReverted !== prevShowReverted ||
      !isEqual(props.versions, prevProps?.versions) ||
      tRowRefs.current == null
    ) {
      const fetchingVersion = _.findWhere(props.versions, {fetching: true});
      const targetVersionId = fetchingVersion?.id || props.openVersionId;
      const node = tRowRefs.current[`version-${targetVersionId}`];
      if (node) {
        const $tbody = $(tbodyRef.current!);

        const height = $tbody.height();
        const top = $tbody.scrollTop();
        const bottom = top + height;

        const nodeHeight = $(node).height();
        const nodeTop = node.offsetTop;
        const nodeBottom = nodeTop + nodeHeight;

        if (!(top < node.offsetTop && nodeBottom < bottom)) {
          $tbody.stop().animate({scrollTop: node.offsetTop - nodeHeight / 2 - height / 2}, 200);
        }
      }
    }
  }, [
    props.openVersionId,
    props.versions,
    prevProps?.openVersionId,
    prevProps?.versions,
    showReverted,
    prevShowReverted,
  ]);

  const openVersion = (versionId: string | number): void => {
    if (props.openVersionId === versionId) {
      return;
    }
    if (props.unsaved || dirty) {
      notifications.warn('Switching versions is disabled while a save is in progress.', {
        id: 'HistoryPanelComponent.openVersion',
      });
      return;
    }
    props.openVersion(versionId);
  };

  const openParent = (): void => {
    fileStates
      .openFolderItem(props.parent!.id, {
        open: true,
        source: 'history-panel-component',
        versionId: props.parent!.versionId,
      })
      .done((fileState: FileState) => {
        fileState.get('toolbar').trigger('openHistoryPanel');
      });
  };

  const revertedInfo = VersionHistoryUtil.processVersions(props.versions);
  // eslint-disable-next-line no-restricted-syntax
  const hasReverts = _.any(_.values(revertedInfo));
  const isFetching = (version: Version): boolean =>
    version.fetching === undefined ? false : version.fetching;
  const hasFetching = _.any(props.versions, isFetching);

  const isActive = (version: Version): boolean => {
    // If one of the versions is fetching, we say that version is active.
    if (hasFetching) {
      return isFetching(version);
    }
    // If the versionId matches openVersionId it is active.
    // OR, we've just finished a save and have a new openVersionId, but we haven't yet
    // fetched the versions, we will say the last version is active.
    const lastVersion = props.versions[props.versions.length - 1];
    return props.openVersionId === version.id || (version === lastVersion && Boolean(props.isLatest));
  };

  // Table layout if @props.showNames:
  //       | name    | user    |    time |
  // else  | user    |              time |
  const columnsClass = showNames ? 'third' : 'half';
  return (
    <Row
      className={classNames(
        css(props.resetToVersion == null && props.cloneFromVersion == null && styles.noMarginBottom),
        'u-tiny-spacing-top'
      )}
      dataTestAttrs={{component: 'HistoryPanel', element: 'root'}}
    >
      <Col md={12}>
        {hasReverts ? (
          <div className="checkbox u-no-spacing-bottom">
            <label className={classNames({disabled: revertedInfo[props.openVersionId]})}>
              <input
                type="checkbox"
                checked={showReverted}
                disabled={revertedInfo[props.openVersionId]}
                onChange={() => {
                  setShowReverted(!showReverted);
                }}
              />
              Show full history
            </label>
          </div>
        ) : null}
        {props.versionsWereTruncated ? (
          <div
            className="alert alert-warning u-small-spacing-bottom"
            {...getTestProps({component: 'HistoryPanel', element: 'truncatedWarning'})}
          >
            Only the most recent 2,000 versions are shown. The full history can be seen in the audit log.
          </div>
        ) : null}
        <Row>
          <Col md={12}>
            <table className="historyVersionTable table table-hover table-condensed scrollable">
              <thead className={css(styles.innerMenuPadding)}>
                <tr>
                  {showNames ? (
                    <th className={classNames('name', columnsClass)}>
                      <div className="text-left">Name</div>
                    </th>
                  ) : undefined}
                  <th className={classNames('user', columnsClass)}>
                    <div className="text-left">User</div>
                  </th>
                  <th className={classNames('time', columnsClass)}>
                    <div className="text-right">Timestamp</div>
                  </th>
                </tr>
              </thead>
              <tbody ref={tbodyRef} className={css(styles.innerMenuPadding)}>
                {props.versions.length === 0 ? (
                  <tr className="version-row">
                    {showNames ? (
                      <td className={classNames('name', columnsClass)}>
                        <div />
                      </td>
                    ) : undefined}
                    <td className={classNames('user', columnsClass)}>
                      <div className="text-left">Loading...</div>
                    </td>
                    <td className={classNames('time', columnsClass)}>
                      <div />
                    </td>
                  </tr>
                ) : (
                  (() => {
                    const result = [];
                    for (let i = props.versions.length - 1; i >= 0; i--) {
                      const version = props.versions[i];
                      const isReverted = revertedInfo[version.id];
                      if (isReverted && !showReverted) {
                        continue;
                      }
                      const isFirstTrackedVersion =
                        props.isFirstTrackedVersion && props.isFirstTrackedVersion(version);
                      const versionKey = `version-${version.id}`;
                      result.push(
                        <tr
                          key={versionKey}
                          ref={(el) => {
                            if (tRowRefs.current != null) {
                              tRowRefs.current[versionKey] = el!;
                            }
                          }}
                          className={classNames(
                            css(isActive(version) ? styles.selectedVersionRow : styles.versionRow),
                            {
                              'version-row': true,
                              'is-reverted': isReverted,
                              'is-fetching': isFetching(version),
                              faded: isReverted,
                            }
                          )}
                          onClick={() => {
                            openVersion(version.id);
                          }}
                          {...getTestProps({
                            component: 'HistoryPanel',
                            element: 'versionRow',
                            index: String(i),
                          })}
                          data-amp-safe={false}
                        >
                          {showNames ? (
                            <td
                              className={classNames(css(styles.rowBorder), 'name', columnsClass)}
                              title={version.name}
                            >
                              <div className="text-left">{version.name}</div>
                            </td>
                          ) : undefined}
                          <td
                            className={classNames(css(styles.rowBorder), 'user', columnsClass)}
                            title={getPartyDisplayString(version.creator)}
                          >
                            <div
                              className="text-left"
                              {...getTestProps({component: 'HistoryPanel', element: 'creatorName'})}
                            >
                              {getPartyDisplayString(version.creator)}
                              {isFirstTrackedVersion && ' \u2022 First tracked version'}
                            </div>
                          </td>
                          <td
                            className={classNames(css(styles.rowBorder), 'time', columnsClass)}
                            title={versionToTime(version, showSeconds)}
                          >
                            <div
                              className="text-right"
                              {...getTestProps({component: 'HistoryPanel', element: 'timestamp'})}
                              {...getTestTimestampProps((version.modified_at || version.modifiedAt)!)}
                            >
                              {versionToTime(version, showSeconds)}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return result;
                  })()
                )}
              </tbody>
            </table>
            {props.hasParent ? (
              <span className="pull-right u-small-spacing-top">
                {'Cloned from '}
                {props.parent != null ? (
                  <a onClick={openParent}>
                    {`${props.parent.name} (${DateUtil.readableTime(new Date(props.parent.modifiedAt))})`}
                  </a>
                ) : (
                  <strong>(Deleted)</strong>
                )}
              </span>
            ) : null}
          </Col>
        </Row>
        {(props.resetToVersion != null || props.cloneFromVersion != null) && (
          <Row className="u-small-spacing-top">
            {props.resetToVersion != null ? (
              <Col sm={6} className={classNames({'col-sm-offset-3': props.cloneFromVersion == null})}>
                <button
                  className="btn btn-af-primary btn-block"
                  onClick={props.resetToVersion}
                  // We disable resetting if they are viewing the current version or if a save is in progress.
                  // This also covers the case where if the entry has been lockedFromExternalChange and if the
                  // entry is dirty, that the user cannot attempt to reset the version (since unsaved will be
                  // true).
                  // We also disable resetting if entry is in review.
                  disabled={
                    props.isLatest ||
                    props.unsaved ||
                    props.isLockedForReview ||
                    props.disableHistoryChanges ||
                    dirty ||
                    !props.permissions?.writable
                  }
                  type="button"
                  {...getTestProps({component: 'HistoryPanel', element: 'resetVersionButton'})}
                >
                  Reset to version
                </button>
              </Col>
            ) : undefined}
            {props.cloneFromVersion != null ? (
              <Col sm={6}>
                <button
                  className={
                    props.resetToVersion != null
                      ? 'btn btn-af-default btn-block'
                      : 'btn btn-af-primary btn-block'
                  }
                  onClick={props.cloneFromVersion}
                  disabled={props.disableHistoryChanges || EntityStore.currentUser() == null}
                  type="button"
                >
                  Clone from version
                </button>
              </Col>
            ) : undefined}
          </Row>
        )}
      </Col>
    </Row>
  );
}

const styles = StyleSheet.create({
  selectedVersionRow: {
    color: '#000DB5',
    backgroundColor: '#E8EDFF',
    fontWeight: 600,
  },
  versionRow: {
    paddingLeft: '8px',
  },
  innerMenuPadding: {
    paddingRight: 5,
  },
  rowBorder: {
    borderTop: 0,
  },
  noMarginBottom: {
    marginBottom: '-20px',
  },
});
